/**
 * Evangelism Service for Admin Dashboard
 * Manages evangelism contacts, interactions, discipleship, and campaigns
 */

import { supabase } from '../lib/supabase'

export type ContactStage = 'prospect' | 'interested' | 'studying' | 'believer' | 'baptized' | 'disciple'

export interface EvangelismContact {
  id: string
  name: string
  phone?: string
  email?: string
  photo?: string
  stage: ContactStage
  location?: {
    latitude: number
    longitude: number
    description?: string
  }
  first_contact_date: string
  evangelist_id: string
  church_id: string
  notes?: string
  prayer_points?: string[]
  next_action?: string
  next_action_date?: string
  last_interaction?: string
  interactions_count?: number
  created_at: string
  updated_at?: string
  // Joined data
  evangelist?: {
    first_name: string
    last_name: string
    email: string
  }
}

export interface Interaction {
  id: string
  contact_id: string
  evangelist_id: string
  type: 'visit' | 'call' | 'message' | 'bible_study' | 'prayer' | 'event' | 'salvation'
  notes?: string
  location?: {
    latitude: number
    longitude: number
  }
  duration_minutes?: number
  outcome?: string
  created_at: string
  evangelist?: {
    first_name: string
    last_name: string
  }
}

export interface EvangelismStats {
  soulsWon: number
  bibleStudies: number
  activeContacts: number
  thisMonth: number
  thisWeek: number
  totalContacts: number
  byStage: Record<ContactStage, number>
  conversionRate: number
  avgTimeToConversion: number
}

export interface ChurchEvangelismStats extends EvangelismStats {
  topEvangelists: Array<{
    id: string
    name: string
    contactsCount: number
    soulsWon: number
  }>
  recentActivity: Interaction[]
  overdueFollowUps: EvangelismContact[]
}

export interface NewConvertIntakeForm {
  id?: string
  contact_id: string
  evangelist_id: string
  church_id: string
  // Personal Info
  age?: number
  marital_status?: 'single' | 'married' | 'divorced' | 'widowed'
  occupation?: string
  address?: string
  // Spiritual Background
  previous_religion?: string
  church_background?: string
  salvation_experience?: string
  salvation_date?: string
  baptism_interest?: 'immediate' | 'learning_more' | 'not_ready' | 'already_baptized'
  // Questions & Needs
  questions?: string[]
  prayer_requests?: string[]
  areas_of_interest?: string[]
  preferred_contact_time?: 'morning' | 'afternoon' | 'evening' | 'weekend'
  preferred_contact_method?: 'call' | 'sms' | 'whatsapp' | 'visit'
  // Family & Social
  family_members_interested?: boolean
  can_host_bible_study?: boolean
  has_transportation?: boolean
  // Discipleship Readiness
  commitment_level?: 'exploring' | 'interested' | 'committed' | 'eager'
  available_for_discipleship?: boolean
  preferred_discipleship_style?: 'one_on_one' | 'group' | 'both'
  // Assignment
  assigned_discipler_id?: string
  assignment_date?: string
  assignment_method?: 'original_evangelist' | 'admin_assigned' | 'pool_assigned' | 'team_assigned'
  created_at?: string
  updated_at?: string
}

export interface DiscipleshipAssignment {
  id: string
  contact_id: string
  original_evangelist_id: string
  assigned_discipler_id: string
  church_id: string
  assignment_reason?: string
  assignment_method: 'original_evangelist' | 'admin_assigned' | 'pool_assigned' | 'team_assigned'
  status: 'active' | 'completed' | 'reassigned' | 'inactive'
  target_completion_date?: string
  progress_percentage?: number
  last_interaction_date?: string
  interactions_count?: number
  created_at: string
  updated_at?: string
  contact?: {
    name: string
  }
  discipler?: {
    first_name: string
    last_name: string
  }
}

export interface OutreachCampaign {
  id: string
  title: string
  description?: string
  date: string
  location?: {
    latitude: number
    longitude: number
    description?: string
  }
  team_members?: string[]
  target_area?: string
  expected_contacts?: number
  actual_contacts?: number
  status: 'planned' | 'active' | 'completed' | 'cancelled'
  church_id: string
  created_at: string
  updated_at?: string
}

class EvangelismService {
  // ===== CONTACTS =====

  /**
   * Get all contacts for a church
   */
  async getAllContacts(
    churchId: string,
    filters?: {
      stage?: ContactStage
      search?: string
      evangelistId?: string
      overdueOnly?: boolean
    }
  ): Promise<EvangelismContact[]> {
    try {
      let query = supabase
        .from('evangelism_contacts')
        .select(`
          *,
          evangelist:profiles!evangelist_id (
            first_name,
            last_name,
            email
          )
        `)
        .eq('church_id', churchId)
        .order('created_at', { ascending: false })

      if (filters?.stage) {
        query = query.eq('stage', filters.stage)
      }

      if (filters?.evangelistId) {
        query = query.eq('evangelist_id', filters.evangelistId)
      }

      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,phone.ilike.%${filters.search}%,email.ilike.%${filters.search}%`)
      }

      const { data, error } = await query

      if (error) throw error

      let contacts = data || []

      // Filter overdue if requested
      if (filters?.overdueOnly) {
        const now = new Date()
        contacts = contacts.filter(c => {
          if (!c.next_action_date) return false
          return new Date(c.next_action_date) < now
        })
      }

      return contacts
    } catch (error) {
      console.error('Error fetching contacts:', error)
      throw error
    }
  }

  /**
   * Get single contact by ID with full details
   */
  async getContact(contactId: string): Promise<EvangelismContact | null> {
    try {
      const { data, error } = await supabase
        .from('evangelism_contacts')
        .select(`
          *,
          evangelist:profiles!evangelist_id (
            first_name,
            last_name,
            email,
            phone
          )
        `)
        .eq('id', contactId)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching contact:', error)
      return null
    }
  }

  /**
   * Create new contact
   */
  async createContact(contact: {
    name: string
    phone?: string
    email?: string
    stage: ContactStage
    evangelist_id: string
    church_id: string
    notes?: string
    next_action?: string
    next_action_date?: string
  }): Promise<EvangelismContact> {
    try {
      const { data, error } = await supabase
        .from('evangelism_contacts')
        .insert({
          name: contact.name,
          phone: contact.phone,
          email: contact.email,
          stage: contact.stage,
          first_contact_date: new Date().toISOString(),
          evangelist_id: contact.evangelist_id,
          church_id: contact.church_id,
          notes: contact.notes,
          next_action: contact.next_action,
          next_action_date: contact.next_action_date,
        })
        .select(`
          *,
          evangelist:profiles!evangelist_id (
            first_name,
            last_name,
            email
          )
        `)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error creating contact:', error)
      throw error
    }
  }

  /**
   * Update contact
   */
  async updateContact(contactId: string, updates: {
    name?: string
    phone?: string
    email?: string
    stage?: ContactStage
    notes?: string
    next_action?: string
    next_action_date?: string
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('evangelism_contacts')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', contactId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating contact:', error)
      throw error
    }
  }

  /**
   * Update contact stage
   */
  async updateContactStage(contactId: string, newStage: ContactStage): Promise<void> {
    try {
      const { error } = await supabase
        .from('evangelism_contacts')
        .update({
          stage: newStage,
          updated_at: new Date().toISOString()
        })
        .eq('id', contactId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating contact stage:', error)
      throw error
    }
  }

  /**
   * Update contact next action
   */
  async updateNextAction(
    contactId: string,
    nextAction: string,
    nextActionDate: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('evangelism_contacts')
        .update({
          next_action: nextAction,
          next_action_date: nextActionDate,
          updated_at: new Date().toISOString()
        })
        .eq('id', contactId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating next action:', error)
      throw error
    }
  }

  /**
   * Delete contact
   */
  async deleteContact(contactId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('evangelism_contacts')
        .delete()
        .eq('id', contactId)

      if (error) throw error
    } catch (error) {
      console.error('Error deleting contact:', error)
      throw error
    }
  }

  // ===== INTERACTIONS =====

  /**
   * Get interaction timeline for a contact
   */
  async getContactInteractions(contactId: string): Promise<Interaction[]> {
    try {
      const { data, error } = await supabase
        .from('evangelism_interactions')
        .select(`
          *,
          evangelist:profiles!evangelist_id (
            first_name,
            last_name
          )
        `)
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching interactions:', error)
      return []
    }
  }

  /**
   * Record new interaction
   */
  async recordInteraction(interaction: Omit<Interaction, 'id' | 'created_at'>): Promise<Interaction> {
    try {
      const { data, error } = await supabase
        .from('evangelism_interactions')
        .insert({
          contact_id: interaction.contact_id,
          evangelist_id: interaction.evangelist_id,
          type: interaction.type,
          notes: interaction.notes,
          location: interaction.location,
          duration_minutes: interaction.duration_minutes,
          outcome: interaction.outcome
        })
        .select()
        .single()

      if (error) throw error

      // Update last interaction date on contact
      await supabase
        .from('evangelism_contacts')
        .update({
          last_interaction: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', interaction.contact_id)

      return data
    } catch (error) {
      console.error('Error recording interaction:', error)
      throw error
    }
  }

  // ===== STATISTICS =====

  /**
   * Get church-wide evangelism statistics
   */
  async getChurchStats(churchId: string): Promise<ChurchEvangelismStats> {
    try {
      // Get all contacts
      const { data: contacts } = await supabase
        .from('evangelism_contacts')
        .select('*, evangelist:profiles!evangelist_id(first_name, last_name)')
        .eq('church_id', churchId)

      if (!contacts) {
        return this.getEmptyChurchStats()
      }

      const now = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      const weekStart = new Date(now.setDate(now.getDate() - now.getDay()))

      // Basic stats
      const soulsWon = contacts.filter(c =>
        c.stage === 'believer' || c.stage === 'baptized' || c.stage === 'disciple'
      ).length

      const bibleStudies = contacts.filter(c => c.stage === 'studying').length
      const activeContacts = contacts.filter(c =>
        c.stage !== 'baptized' && c.stage !== 'disciple'
      ).length

      const thisMonth = contacts.filter(c => new Date(c.created_at) >= monthStart).length
      const thisWeek = contacts.filter(c => new Date(c.created_at) >= weekStart).length

      // By stage
      const byStage: Record<ContactStage, number> = {
        prospect: 0,
        interested: 0,
        studying: 0,
        believer: 0,
        baptized: 0,
        disciple: 0
      }

      contacts.forEach(c => {
        byStage[c.stage as ContactStage]++
      })

      // Conversion rate
      const converted = byStage.believer + byStage.baptized + byStage.disciple
      const conversionRate = contacts.length > 0 ? (converted / contacts.length) * 100 : 0

      // Average time to conversion (simplified)
      const avgTimeToConversion = 30 // Days - would need more complex calculation

      // Top evangelists
      const evangelistCounts = new Map<string, { name: string, count: number, soulsWon: number }>()

      contacts.forEach(c => {
        const id = c.evangelist_id
        const name = c.evangelist ? `${c.evangelist.first_name} ${c.evangelist.last_name}` : 'Unknown'
        const isSoulWon = c.stage === 'believer' || c.stage === 'baptized' || c.stage === 'disciple'

        if (!evangelistCounts.has(id)) {
          evangelistCounts.set(id, { name, count: 0, soulsWon: 0 })
        }

        const stats = evangelistCounts.get(id)!
        stats.count++
        if (isSoulWon) stats.soulsWon++
      })

      const topEvangelists = Array.from(evangelistCounts.entries())
        .map(([id, stats]) => ({
          id,
          name: stats.name,
          contactsCount: stats.count,
          soulsWon: stats.soulsWon
        }))
        .sort((a, b) => b.soulsWon - a.soulsWon)
        .slice(0, 10)

      // Recent activity
      const { data: recentInteractions } = await supabase
        .from('evangelism_interactions')
        .select(`
          *,
          evangelist:profiles!evangelist_id (first_name, last_name)
        `)
        .eq('church_id', churchId)
        .order('created_at', { ascending: false })
        .limit(20)

      // Overdue follow-ups
      const overdueFollowUps = contacts.filter(c => {
        if (!c.next_action_date) return false
        return new Date(c.next_action_date) < now
      })

      return {
        soulsWon,
        bibleStudies,
        activeContacts,
        thisMonth,
        thisWeek,
        totalContacts: contacts.length,
        byStage,
        conversionRate,
        avgTimeToConversion,
        topEvangelists,
        recentActivity: recentInteractions || [],
        overdueFollowUps
      }
    } catch (error) {
      console.error('Error fetching church stats:', error)
      return this.getEmptyChurchStats()
    }
  }

  private getEmptyChurchStats(): ChurchEvangelismStats {
    return {
      soulsWon: 0,
      bibleStudies: 0,
      activeContacts: 0,
      thisMonth: 0,
      thisWeek: 0,
      totalContacts: 0,
      byStage: {
        prospect: 0,
        interested: 0,
        studying: 0,
        believer: 0,
        baptized: 0,
        disciple: 0
      },
      conversionRate: 0,
      avgTimeToConversion: 0,
      topEvangelists: [],
      recentActivity: [],
      overdueFollowUps: []
    }
  }

  // ===== DISCIPLESHIP =====

  /**
   * Get all discipleship assignments for church
   */
  async getDiscipleshipAssignments(churchId: string): Promise<DiscipleshipAssignment[]> {
    try {
      const { data, error } = await supabase
        .from('evangelism_discipleship')
        .select(`
          *,
          contact:evangelism_contacts!contact_id (name),
          discipler:profiles!assigned_discipler_id (first_name, last_name)
        `)
        .eq('church_id', churchId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching discipleship assignments:', error)
      return []
    }
  }

  /**
   * Assign discipler to contact
   */
  async assignDiscipler(
    contactId: string,
    disciplerId: string,
    churchId: string,
    originalEvangelistId: string,
    assignmentReason?: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('evangelism_discipleship')
        .insert({
          contact_id: contactId,
          original_evangelist_id: originalEvangelistId,
          assigned_discipler_id: disciplerId,
          church_id: churchId,
          assignment_reason: assignmentReason,
          assignment_method: 'admin_assigned',
          status: 'active'
        })

      if (error) throw error
    } catch (error) {
      console.error('Error assigning discipler:', error)
      throw error
    }
  }

  // ===== CAMPAIGNS =====

  /**
   * Get all outreach campaigns for church
   */
  async getCampaigns(churchId: string): Promise<OutreachCampaign[]> {
    try {
      const { data, error } = await supabase
        .from('evangelism_campaigns')
        .select('*')
        .eq('church_id', churchId)
        .order('date', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching campaigns:', error)
      return []
    }
  }

  /**
   * Create new campaign
   */
  async createCampaign(campaign: Omit<OutreachCampaign, 'id' | 'created_at' | 'updated_at'>): Promise<OutreachCampaign> {
    try {
      const { data, error } = await supabase
        .from('evangelism_campaigns')
        .insert(campaign)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error creating campaign:', error)
      throw error
    }
  }

  /**
   * Update campaign
   */
  async updateCampaign(campaignId: string, updates: Partial<OutreachCampaign>): Promise<void> {
    try {
      const { error } = await supabase
        .from('evangelism_campaigns')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', campaignId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating campaign:', error)
      throw error
    }
  }

  // ===== EVANGELISTS =====

  /**
   * Get all evangelists for church (users who can be assigned contacts)
   */
  async getEvangelists(churchId: string): Promise<Array<{ id: string, name: string, email: string }>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .eq('church_id', churchId)
        .in('role', ['admin', 'leader', 'pastor', 'member'])
        .order('first_name')

      if (error) throw error

      return (data || []).map(p => ({
        id: p.id,
        name: `${p.first_name} ${p.last_name}`,
        email: p.email
      }))
    } catch (error) {
      console.error('Error fetching evangelists:', error)
      return []
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Get stage display label
   */
  getStageLabel(stage: ContactStage): string {
    const labels: Record<ContactStage, string> = {
      prospect: 'Prospect',
      interested: 'Interested',
      studying: 'Studying',
      believer: 'Believer',
      baptized: 'Baptized',
      disciple: 'Disciple'
    }
    return labels[stage]
  }

  /**
   * Get interaction type label
   */
  getInteractionLabel(type: string): string {
    const labels: Record<string, string> = {
      visit: 'Visit',
      call: 'Phone Call',
      message: 'Message',
      bible_study: 'Bible Study',
      prayer: 'Prayer',
      event: 'Event',
      salvation: 'Salvation'
    }
    return labels[type] || type
  }
}

export default new EvangelismService()
